/**
 * Mémoire LOCALE (ce poste, cet utilisateur) de la fermeture de la fenêtre d'alerte SAV : l'heure de fermeture et les identifiants des
 * dossiers alors signalés (aucune donnée de client). Fermée, la fenêtre ne revient pas avant le rythme réglé (4 h), sauf si un NOUVEAU
 * dossier entre dans l'alerte. Le stockage du navigateur peut être absent ou lever une erreur : tout est protégé par try/catch et la
 * mémoire du composant prend le relais (jamais de fenêtre en boucle).
 */

export interface AlertDismissal {
  at: number;
  ids: string[];
}

const KEY_PREFIX = 'luxori:sav-alert-dismissed:';
export const alertStorageKey = (userId: string): string => `${KEY_PREFIX}${userId}`;

const getStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

export function readDismissal(userId: string): AlertDismissal | null {
  try {
    const raw = getStorage()?.getItem(alertStorageKey(userId));
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<AlertDismissal>;
    if (
      typeof v?.at !== 'number' ||
      !Number.isFinite(v.at) ||
      !Array.isArray(v.ids) ||
      !v.ids.every(i => typeof i === 'string')
    )
      return null;
    return { at: v.at, ids: v.ids.slice(0, 500) };
  } catch {
    return null;
  }
}

export function writeDismissal(userId: string, dismissal: AlertDismissal): void {
  try {
    getStorage()?.setItem(alertStorageKey(userId), JSON.stringify(dismissal));
  } catch {
    // stockage indisponible (mode privé, quota, accès refusé) : la mémoire du composant suffit pour cette session
  }
}

/**
 * La fenêtre doit-elle s'ouvrir ? Oui s'il y a des dossiers à signaler et : jamais fermée, ou fermée depuis au moins `repeatHours`,
 * ou un dossier qui n'était pas dans la liste lors de la fermeture vient d'entrer dans l'alerte.
 */
export function shouldOpenAlert(args: {
  alertIds: string[];
  dismissal: AlertDismissal | null;
  now: number;
  repeatHours: number;
}): boolean {
  const { alertIds, dismissal, now, repeatHours } = args;
  if (alertIds.length === 0) return false;
  if (!dismissal) return true;
  if (now - dismissal.at >= repeatHours * 3_600_000) return true;
  const known = new Set(dismissal.ids);
  return alertIds.some(id => !known.has(id));
}

/** Pas d'ouverture pendant une autre fenêtre (ex. rédaction d'une réponse) ni pendant la saisie dans un champ. */
export function canInterruptUser(doc: Document = document): boolean {
  if (doc.querySelector('[role="dialog"]')) return false;
  const el = doc.activeElement as HTMLElement | null;
  if (!el) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return false;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    return [
      'button',
      'submit',
      'checkbox',
      'radio',
      'reset',
      'image',
      'file',
      'range',
      'color',
    ].includes(type);
  }
  return !el.isContentEditable;
}
